/**
 * Pure delta-application logic for an in-flight assistant turn.
 *
 * Lives outside ChatPage.tsx so unit tests can import without pulling
 * in SolidJS client-only APIs (createSignal etc.) which throw under
 * vitest's jsdom env without a SolidJS root.
 */
import type { ChatChunk, ChatMessage } from "@/api/generated";
import type { BrowseWatch } from "./browseWatch";

export interface ToolCallView {
  id: string;
  name: string;
  server: string;
  args: string;
  status: "pending" | "ok" | "error";
  result?: unknown;
  error?: string;
  latency_ms?: number;
  /** Live TinyFish browser iframe(s). In-memory only; not persisted. */
  watch?: BrowseWatch[];
}

export interface ChatTurn {
  id: string;
  role: ChatMessage["role"];
  content: string;
  reasoning: string;
  pending: boolean;
  error?: string;
  tool_calls: ToolCallView[];
  /** UUIDs of pgvector chunks injected as RAG context on this assistant
   *  turn. Always `[]` outside of RAG-augmented turns. Populated by
   *  `hydrateTurns` from `MessageRecord.retrieved_chunk_ids` only —
   *  the SSE stream does not carry these ids, so during streaming this
   *  array stays empty and the context-used disclosure stays hidden.
   *  It appears once `refetchTranscript` re-hydrates the turn post-settle. */
  retrieved_chunk_ids: string[];
}

export function applyDeltaToTurn(turn: ChatTurn, chunk: ChatChunk): ChatTurn {
  if (chunk.kind === "delta") {
    // Wire shape skips empty fields (`#[serde(skip_serializing_if = "String::is_empty")]`),
    // so `chunk.content` / `chunk.reasoning_content` may be undefined for
    // pure-content or pure-reasoning deltas. Coalesce to empty string
    // before concatenating, otherwise JS appends the literal "undefined".
    return {
      ...turn,
      content: turn.content + (chunk.content ?? ""),
      reasoning: turn.reasoning + (chunk.reasoning_content ?? ""),
    };
  }
  if (chunk.kind === "tool_call_start") {
    const next: ToolCallView = {
      id: chunk.id,
      name: chunk.name,
      server: chunk.server,
      args: "",
      status: "pending",
    };
    return { ...turn, tool_calls: [...turn.tool_calls, next] };
  }
  if (chunk.kind === "tool_call_args_delta") {
    return {
      ...turn,
      tool_calls: turn.tool_calls.map((t) =>
        t.id === chunk.id ? { ...t, args: t.args + chunk.args_delta } : t,
      ),
    };
  }
  if (chunk.kind === "tool_call_result") {
    return {
      ...turn,
      tool_calls: turn.tool_calls.map((t) =>
        t.id === chunk.id
          ? {
              ...t,
              status: chunk.ok ? "ok" : "error",
              result: chunk.content,
              error: chunk.error,
              latency_ms: chunk.latency_ms,
            }
          : t,
      ),
    };
  }
  if (chunk.kind === "done") {
    return { ...turn, pending: false };
  }
  if (chunk.kind === "error") {
    return { ...turn, pending: false, error: chunk.message };
  }
  return turn;
}
